import type { AdminNavLink, VisibleAdminNavGroup } from './adminNavGroups';

/**
 * A place the palette can send you. Today every destination is a nav page, so the
 * list is derived entirely from what `adminNavGroups` already declares — the palette
 * adds no routes of its own and inherits the nav's permission filtering for free.
 */
export type AdminSearchDestination = {
  icon: string;
  label: string;
  route: string;
  /** Nav group the page sits in, shown beside the result for context. */
  group: string;
};

export type AdminSearchResult = AdminSearchDestination & { score: number };

/** Splits on the separators that appear in labels and routes alike. */
const WORD_SPLIT = /[\s\-_./@]+/;
/** Single-character form of WORD_SPLIT, for classifying one position at a time. */
const WORD_SEPARATOR_CHAR = /[\s\-_./@]/;

const wordsOf = (value: string): string[] => value.split(WORD_SPLIT).filter(Boolean);

const NO_MATCH = -1;

/**
 * Which word (by index, 0-based) each character of `haystack` belongs to; a
 * separator character gets -1. Lets the subsequence tier check whether a match
 * actually crosses from one word into another, rather than being scattered
 * within a single one — see the tier's own comment for why that distinction is
 * the whole point.
 */
const wordIdsOf = (haystack: string): number[] => {
  const ids: number[] = new Array(haystack.length);
  let currentId = -1;
  let inWord = false;
  for (let i = 0; i < haystack.length; i += 1) {
    if (WORD_SEPARATOR_CHAR.test(haystack[i])) {
      ids[i] = -1;
      inWord = false;
      continue;
    }
    if (!inWord) {
      currentId += 1;
      inWord = true;
    }
    ids[i] = currentId;
  }
  return ids;
};

export type ScoreLabelOptions = {
  /**
   * The subsequence tier is the loosest one, and it gets looser as the haystack
   * gets longer: any string long enough tends to contain SOME subsequence of a
   * short query somewhere. Fine for a label a person is directly trying to
   * recall (a page name, a profile slug). Not fine for shared metadata that many
   * unrelated items all carry — e.g. every admin profile's middleware pipeline
   * includes a step called "conversation_compression", which subsequence-matches
   * "pri" (p-r-·-·-·-i) purely by accident, and would make every profile "match"
   * regardless of what it actually is. Callers scoring that kind of field pass
   * `allowSubsequence: false` to require at least a real substring.
   */
  allowSubsequence?: boolean;
};

/**
 * How well a human-facing label matches what was typed, ranked:
 * whole-string prefix > word prefix > substring > initials > tight subsequence.
 *
 * The subsequence tier only accepts a match that crosses from one word into
 * another — e.g. "crj" hitting "Cron Jobs" (c,r in "Cron", j in "Jobs") reads as
 * a genuine abbreviation. Without that requirement, "pri" also subsequence-
 * matches "Email Preview" (p-r-·-·-i, all inside the single word "preview") —
 * coincidence, not intent, and it doesn't even need a long string to happen:
 * short, common words routinely contain a short query's letters in order.
 * Requiring more than one word turns "does this look like an abbreviation" into
 * a checkable rule instead of a hope.
 *
 * The tightness bound (matched characters landing within roughly twice the
 * query length) is the other half: unbounded, "dana" matches "transcript-
 * coverage" (d…a…n…a scattered across the whole string), and that junk then
 * outranks the thing the user actually meant.
 */
export const scoreLabel = (
  label: string,
  query: string,
  { allowSubsequence = true }: ScoreLabelOptions = {}
): number => {
  if (!query) return 0;

  const haystack = label.toLowerCase();
  const needle = query.toLowerCase();

  const index = haystack.indexOf(needle);
  if (index === 0) return 1000;

  const words = wordsOf(haystack);
  if (words.some((word) => word.startsWith(needle))) return 850;
  if (index > 0) return 700 - Math.min(index, 60);

  if (needle.length >= 2) {
    const initials = words.map((word) => word[0]).join('');
    if (initials.startsWith(needle)) return 620;
  }

  if (allowSubsequence && needle.length >= 3) {
    const wordIds = wordIdsOf(haystack);
    let matched = 0;
    let start = NO_MATCH;
    for (let position = 0; position < haystack.length; position += 1) {
      if (haystack[position] !== needle[matched]) continue;
      if (matched === 0) start = position;
      matched += 1;
      if (matched === needle.length) {
        const isTight = position - start <= needle.length * 2;
        const crossesAWord = wordIds[start] !== wordIds[position];
        return isTight && crossesAWord ? 260 : NO_MATCH;
      }
    }
  }

  return NO_MATCH;
};

/**
 * Routes are machine text, so they match on substring only — never fuzzily. Someone
 * typing `/admin/cron` wants that page; nobody typing a name wants a route whose
 * path happens to contain the same letters in order.
 */
export const scoreRoute = (route: string, query: string): number => {
  if (!query) return 0;
  const index = route.toLowerCase().indexOf(query.toLowerCase());
  return index === NO_MATCH ? NO_MATCH : 640 - Math.min(index, 40);
};

export const scoreDestination = (destination: AdminSearchDestination, query: string): number =>
  Math.max(scoreLabel(destination.label, query), scoreRoute(destination.route, query));

const toDestination = (link: AdminNavLink, group: string): AdminSearchDestination => ({
  icon: link.icon,
  label: link.label,
  route: link.route,
  group,
});

/**
 * Flattens the already permission-filtered nav groups into a search corpus.
 *
 * `homeLink` is passed separately because `AdminNav` renders it outside the groups
 * and gates it on its own permission check.
 */
export const adminSearchDestinations = (
  groups: readonly VisibleAdminNavGroup[],
  homeLink?: AdminNavLink | null
): AdminSearchDestination[] => {
  const destinations = homeLink ? [toDestination(homeLink, 'Admin')] : [];
  for (const group of groups) {
    for (const link of group.links) {
      destinations.push(toDestination(link, group.label));
    }
  }
  return destinations;
};

export const ADMIN_SEARCH_RESULT_LIMIT = 8;

/**
 * With no query this is the nav's own order, truncated — the palette opens onto a
 * useful list rather than an empty box. With a query it is score-ordered; `sort` is
 * stable, so equal scores keep nav order too.
 */
export const searchAdminDestinations = (
  destinations: readonly AdminSearchDestination[],
  query: string,
  limit: number = ADMIN_SEARCH_RESULT_LIMIT
): AdminSearchResult[] => {
  const trimmed = query.trim();

  if (!trimmed) {
    return destinations.slice(0, limit).map((destination) => ({ ...destination, score: 0 }));
  }

  return destinations
    .map((destination) => ({ ...destination, score: scoreDestination(destination, trimmed) }))
    .filter((result) => result.score > NO_MATCH)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
};
